//@ts-check

/**
 * @typedef {import("./coreTypes").QuerySpec} QuerySpec
 * @typedef {import("./coreTypes").CampaignObject} CampaignObject
 * @typedef {import("./coreTypes").ResultAtom} ResultAtom
 * @typedef {import('./coreTypes').JSResultAtom} JSResultAtom
 */

import "../styles/WebLandingPage.css";
import "../styles/MobileWebLandingPage.css";
import "../styles/WebLoadingText.css";
import "../styles/MobileDonorCard.css";


// @ts-ignore
import luLogo from "../images/LU.svg";
// @ts-ignore
import drwText from "../images/donor_recognition_wall.svg";
// @ts-ignore
import searchIcon from "../images/search_icon--white.svg";
// @ts-ignore
import removeSearchIcon from "../images/remove_search.svg";


import React, { useState, useEffect } from "react";

import ResultsLoadingManagerWebInstance from "./ResultsLoadingManagerWebInstance";
import { resultAtomsToParsedDonors } from "./paginator";
import { getResultsFor } from "./GetQueryResultsFunctions";
import DonorCardWebLayout from "./DonorCardWebLayout";
import MobileChooseCampaignLightbox from "./MobileChooseCampaignLightbox";
import MobileWebResultsCampaignPage from "./MobileWebResultsCampaignPage";
import CampaignList, { getQueryName } from "./CampaignList";
import { pushDonorListIsPopulated } from "./AnalyticsFunctions";

/**
 * @param {{}} props 
 * @returns {JSX.Element | null}
 */
function MobileWebLandingPage(props) {

    const [searchString, setSearchString] = useState("");
    const [lastSearchStringUsed, setLastSearchStringUsed] = useState(searchString);


    const [resultsDonorName, setResultsDonorName] = useState("");

    /**
     * @type {[JSResultAtom[], React.Dispatch<React.SetStateAction<JSResultAtom[]>>]}
     */
    // @ts-ignore
    const [donors, setDonors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [startedAtLeastOneSearch, setStartedAtLeastOneSearch] = useState(false);
    const [isLandingPage, setIsLandingPage] = useState(true);
    /**
     * @type {[CampaignObject|null, React.Dispatch<React.SetStateAction<CampaignObject|null>>]}
     */
    // @ts-ignore
    const [campaignObjectIfCampaignPage, setCampaignObjectIfCampaignPage] = useState(null);

    const [isError, setIsError] = useState(false);


    const shouldDisplayRemoveSearchIcon = 
        (lastSearchStringUsed !== "") && (lastSearchStringUsed === searchString);

    const [showCampaignLightBox, setShowCampaignLightBox] = useState(false);


    const comb = ResultsLoadingManagerWebInstance.generateCombinedMethodsFromSetter(setDonors);
    const generateLoadingIdAndSetAsActive = comb.generateLoadingIdAndSetAsActive;
    const setDonorsIfCurrentLoadingId = comb.setResultsIfCurrentLoadingId;   



    useEffect(() => {

        /** @type {QuerySpec} */
        const querySpec = [['donor_search_string', resultsDonorName]];

        setDonors([]);

        if (resultsDonorName === "") { 
            setIsLoading(false);
            return;
        }

        const id = generateLoadingIdAndSetAsActive();
        setIsLoading(true);

        const errorHandler = _ => { setIsLoading(false); setIsError(true); };
        const resultsObj = getResultsFor(querySpec, null);

        /**
         * @type {function(ResultAtom[]):void}
         */
        let nextHandler;
    
        nextHandler = (res) => {
            if (ResultsLoadingManagerWebInstance.getCurrentLoadingId() === id
                && res.length > 0) {
                setDonors(d => d.concat(resultAtomsToParsedDonors(res)));
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            }
            else {
                setIsLoading(false);
            }
        };

        resultsObj.initial.then(res => {
            setDonorsIfCurrentLoadingId(id, resultAtomsToParsedDonors(res));
            setIsError(false);
            
            if (res.length > 0) {
                pushDonorListIsPopulated(true);
                resultsObj.getNext().then(nextHandler).catch(errorHandler);
            } else {
                setIsLoading(false);
                pushDonorListIsPopulated(false);
            }
        }).catch(errorHandler);
    
    // eslint-disable-next-line
    }, [resultsDonorName]);

    

    const performSearch = e => { 
        setStartedAtLeastOneSearch(true);
        setLastSearchStringUsed(searchString);
        setResultsDonorName(searchString);
    };

    
    const clearOutSearch = e => { 
        setLastSearchStringUsed(""); 
        setSearchString("");
    };


    /**
     * @param {string} campQueryName 
     */
    const selectCampaign = (campQueryName) => {
        setCampaignObjectIfCampaignPage(getCampaignObjectFromQueryName(campQueryName));
        setIsLandingPage(false);
    };

    /**
     * @param {string} donorName 
     * @return {void}
     */
    const performDonorSearchFromCampaignPage = (donorName) => {
        setStartedAtLeastOneSearch(true);
        setIsLandingPage(true);
        setLastSearchStringUsed(donorName);
        setSearchString(donorName);
        setResultsDonorName(donorName);
    };

    const landingPage = (
        <>
        {showCampaignLightBox && 
            <MobileChooseCampaignLightbox 
                cancelLightbox={() => { setShowCampaignLightBox(false); }} 
                selectCampaign={selectCampaign} />}
        <div className="web-app mobile-web">
            {/** @ts-ignore */}
            <div style={containerStyle}>      
                <img src={luLogo} className="mobile-web-app-logo-name" alt="Liberty University"/>
                <img src={drwText} className="mobile-web-drw-text-name" alt="Donor Recognition Wall"/>
                <p style={{ marginTop: 20, }}></p>
                <div className="mobile-web-res__search__container">
                    <input 
                        type="text" 
                        className="mobile-web-res__search__field"
                        aria-label="Search for a donor by name" 
                        placeholder="Search for a donor by name"
                        value={searchString} 
                        onChange={e => setSearchString(e.target.value)} 
                        onKeyUp={e => {
                                if (e.key === "Enter") { performSearch(); }
                            }} />
                    <input type="button" className="mobile-web-res__search__btn" aria-label="Submit search" onClick={performSearch} />
                    <img src={searchIcon} className="mobile-web-res__search__icon" alt="Search" />
                    { shouldDisplayRemoveSearchIcon && 
                        <img 
                            src={removeSearchIcon} 
                            className="mobile-web-res__search__remove-search"
                            onClick={clearOutSearch} alt="Remove search text" />}
                </div>
                <div className="mobile-web-res__or-container">
                    <div className="mobile-web-res__or-divider" />
                    <p className="mobile-web-res__or-text">or</p>
                    <div className="mobile-web-res__or-divider" />
                </div>
                <input 
                    type="button"
                    aria-haspopup="true" 
                    className="mobile-web-res__campaign-btn" 
                    value="Browse By Campaign" 
                    onClick={() => { setShowCampaignLightBox(true); } }/>
            </div>
            <p style={{ paddingBottom: 20 }}></p>
            { !isError && !isLoading && donors.length === 0 
                && startedAtLeastOneSearch && <p style={errorStyle}>There are no results.</p>}
            { !isError && <DonorCardWebLayout donors={donors} singleColumn={true} /> }
            { !isLoading && isError && <p style={errorStyle}>There was an error fetching the list of donors.</p> }
            { (isLoading)
                ? <div className="web-loading-text__container">
                    <p className="web-loading-text__loading">Loading</p>
                    <p className="web-loading-text__dot-1"> . </p>
                    <p className="web-loading-text__dot-2"> . </p>
                    <p className="web-loading-text__dot-3"> . </p>
                  </div> 
                : null }
        </div>
        </>
    );

    const campaignPage = (
        <MobileWebResultsCampaignPage 
            campaign={campaignObjectIfCampaignPage} 
            onCampaignChanged={co => setCampaignObjectIfCampaignPage(co)}
            performDonorSearch={performDonorSearchFromCampaignPage}/>);

    return (isLandingPage) ? landingPage : campaignPage;
}

const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 40,
    color: 'white',
};

const errorStyle = {
    fontFamily: 'Arial',
    color: 'white',
    fontSize: 14,
};

/**
 * @param {string} queryName 
 * @returns {CampaignObject|null}
 */
function getCampaignObjectFromQueryName(queryName) {
    const res = CampaignList.find(c => getQueryName(c) === queryName );
    if (res === undefined) return null;
    else return res;
}

export default MobileWebLandingPage;
